// Informes de ventas: por cliente, por dia y top de productos, con filtros
// de rango (dia/semana/mes o fechas elegidas). Equivale a la hoja "Informe".

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { hoyMontevideo, rangoFechas } from "../../lib/fecha";
import { iconoImpresora } from "../iconos";
import type { InformeVentas } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}
function ddmmaaaa(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

export async function renderInformes(contenedor: HTMLElement): Promise<void> {
  const hoy = hoyMontevideo();
  const inicioMes = hoy.slice(0, 8) + "01";

  contenedor.innerHTML = `
    <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="titulo-pagina">Informes</h1>
        <p class="subtitulo">Ventas por cliente, por día y productos más vendidos.</p>
      </div>
      <div class="no-print flex flex-wrap items-end gap-2">
        <label><span class="etiqueta">Desde</span><input id="desde" type="date" class="campo" value="${inicioMes}" /></label>
        <label><span class="etiqueta">Hasta</span><input id="hasta" type="date" class="campo" value="${hoy}" /></label>
        <div class="flex gap-1">
          <button class="btn-sec px-2 py-1 text-xs" data-rango="dia">Día</button>
          <button class="btn-sec px-2 py-1 text-xs" data-rango="semana">Semana</button>
          <button class="btn-sec px-2 py-1 text-xs" data-rango="mes">Mes</button>
          <button class="btn-sec px-2 py-1 text-xs" data-rango="todo">Todo</button>
        </div>
        <button id="imprimir" class="btn-sec">${iconoImpresora} Imprimir</button>
      </div>
    </div>
    <div id="salida" class="space-y-4"></div>`;

  const inpDesde = contenedor.querySelector("#desde") as HTMLInputElement;
  const inpHasta = contenedor.querySelector("#hasta") as HTMLInputElement;
  const salida = contenedor.querySelector("#salida") as HTMLElement;

  async function cargar(): Promise<void> {
    salida.innerHTML = `<div class="tarjeta text-masa-400">Cargando...</div>`;
    const q = new URLSearchParams();
    if (inpDesde.value) q.set("desde", inpDesde.value);
    if (inpHasta.value) q.set("hasta", inpHasta.value);
    try {
      const r = await api.get<InformeVentas>(`/api/informes/ventas?${q.toString()}`);
      pintar(r);
    } catch (e) {
      salida.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  function pintar(r: InformeVentas): void {
    if (r.por_cliente.length === 0) {
      salida.innerHTML = `<div class="tarjeta text-center text-masa-400">Sin ventas en el periodo elegido.</div>`;
      return;
    }
    const totalGeneral = r.por_cliente.reduce((a, c) => a + c.total, 0);
    const totalPedidos = r.por_cliente.reduce((a, c) => a + c.pedidos, 0);

    const clientes = `
      <div class="tarjeta">
        <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Ventas por cliente</h2>
        <div class="overflow-x-auto"><table class="tabla">
          <thead><tr><th>Cliente</th><th class="text-right">Pedidos</th><th class="text-right">Total</th></tr></thead>
          <tbody>${r.por_cliente
            .map(
              (c) => `<tr><td>${esc(c.cliente)}</td>
                <td class="text-right">${c.pedidos}</td>
                <td class="text-right tabular-nums">${formatearPesos(c.total)}</td></tr>`,
            )
            .join("")}</tbody>
          <tfoot><tr class="border-t-2 border-masa-300 font-bold">
            <td class="px-3 py-2">TOTAL</td>
            <td class="px-3 py-2 text-right">${totalPedidos}</td>
            <td class="px-3 py-2 text-right tabular-nums">${formatearPesos(totalGeneral)}</td>
          </tr></tfoot>
        </table></div>
      </div>`;

    const dias = `
      <div class="tarjeta">
        <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Ventas por día</h2>
        <div class="overflow-x-auto"><table class="tabla">
          <thead><tr><th>Fecha</th><th class="text-right">Pedidos</th><th class="text-right">Total</th></tr></thead>
          <tbody>${r.por_fecha
            .map(
              (f) => `<tr><td class="whitespace-nowrap">${ddmmaaaa(f.fecha)}</td>
                <td class="text-right">${f.pedidos}</td>
                <td class="text-right tabular-nums">${formatearPesos(f.total)}</td></tr>`,
            )
            .join("")}</tbody>
        </table></div>
      </div>`;

    const top = `
      <div class="tarjeta">
        <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Productos más vendidos</h2>
        <div class="overflow-x-auto"><table class="tabla">
          <thead><tr><th>Artículo</th><th class="text-right">Bultos</th><th class="text-right">Importe</th></tr></thead>
          <tbody>${r.top_productos
            .map(
              (p) => `<tr><td>${esc(p.descripcion)} <span class="text-xs text-masa-400">#${p.codigo}</span></td>
                <td class="text-right">${p.bultos}</td>
                <td class="text-right tabular-nums">${formatearPesos(p.importe)}</td></tr>`,
            )
            .join("")}</tbody>
        </table></div>
      </div>`;

    const periodo =
      r.desde || r.hasta
        ? `${ddmmaaaa(r.desde ?? "")} — ${ddmmaaaa(r.hasta ?? "") || "hoy"}`
        : "Todo el historial";
    salida.innerHTML = `
      <div class="text-sm text-masa-500">Periodo: <b>${esc(periodo)}</b></div>
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        ${clientes}
        <div class="space-y-4">${dias}${top}</div>
      </div>`;
  }

  inpDesde.addEventListener("change", () => void cargar());
  inpHasta.addEventListener("change", () => void cargar());
  contenedor.querySelectorAll<HTMLButtonElement>("[data-rango]").forEach((b) => {
    b.addEventListener("click", () => {
      const tipo = b.dataset.rango as "dia" | "semana" | "mes" | "todo";
      if (tipo === "todo") { inpDesde.value = ""; inpHasta.value = ""; }
      else {
        const ref = inpHasta.value || hoy;
        const [d, h] = rangoFechas(ref, tipo);
        inpDesde.value = d; inpHasta.value = h;
      }
      void cargar();
    });
  });
  contenedor.querySelector("#imprimir")?.addEventListener("click", () => window.print());

  await cargar();
}
